import React, { useState } from "react";


import { DatePicker } from "./DatePicker";
import { PriorityPicker } from "./PriorityPicker";
import { LabelTag } from "./LabelTag";
import { UserSelectDropdown } from "./UserSelectDropdown";
import { RecurrencePicker } from "./RecurrencePicker";
import { updateTask, addComment } from "../../firebase/tasks";

/**
 * Helper to format timestamps inside the right panel for footer metadata.
 */
const formatMetadataTime = (ts) => {
  if (!ts) return "N/A";
  let date;
  if (typeof ts.toDate === "function") {
    date = ts.toDate();
  } else if (ts.seconds) {
    date = new Date(ts.seconds * 1000);
  } else {
    date = new Date(ts);
  }
  return date.toLocaleString();
};

/**
 * TaskRightPanel Component
 * 
 * Props:
 * - task: Full task object
 * - users: Array of system user profiles
 * - labels: Array of all system labels
 * - user: Active user session
 * - onFieldSaved: (field: string, newValue: any) => void
 */
export function TaskRightPanel({ task, users = [], labels = [], user, onFieldSaved }) {
  const [hoverWarn, setHoverWarn] = useState(null);

  // Helper to map uid to user name
  const getUserName = (uid) => {
    if (!uid) return "Unassigned";
    const matched = users.find((u) => u.uid === uid);
    return matched ? matched.name : "Unknown User";
  };

  const handleAssigneeChange = async (newAssigneeUid) => {
    const finalAssignee = newAssigneeUid || null;
    if (finalAssignee === task.assigneeUid) return;

    try {
      const oldName = getUserName(task.assigneeUid);
      const newName = getUserName(finalAssignee);

      // Log system comment for assignment change
      const authorName = user?.displayName || user?.email || "System";
      await addComment(task.id, {
        authorUid: user?.uid || "system",
        authorName: authorName,
        isSystem: true,
        type: "assignment_change",
        text: `Assignee changed from "${oldName}" to "${newName}" by ${authorName}`,
        oldValue: oldName,
        newValue: newName,
      });

      await updateTask(task.id, {
        assigneeUid: finalAssignee,
        updatedByUid: user?.uid || "system"
      });

      if (onFieldSaved) {
        onFieldSaved("assignee", finalAssignee);
      }
    } catch (err) {
      console.error("Error saving assignee:", err);
    }
  };

  const handleDateChange = async (newDate) => {
    if (newDate === task.dueDate) return;
    try {
      const authorName = user?.displayName || user?.email || "System";
      await addComment(task.id, {
        authorUid: user?.uid || "system",
        authorName: authorName,
        isSystem: true,
        type: "date_change",
        text: `Due date changed from "${task.dueDate || "none"}" to "${newDate || "none"}" by ${authorName}`,
        oldValue: task.dueDate || null,
        newValue: newDate || null,
      });
      await updateTask(task.id, {
        dueDate: newDate,
        updatedByUid: user?.uid || "system"
      });
      if (onFieldSaved) {
        onFieldSaved("dueDate", newDate);
      }
    } catch (err) {
      console.error("Error saving due date:", err);
    }
  };

  const handleStatusChange = async (newStatus) => {
    if (newStatus === task.status) return;
    try {
      const authorName = user?.displayName || user?.email || "System";
      await addComment(task.id, {
        authorUid: user?.uid || "system",
        authorName: authorName,
        isSystem: true,
        type: "state_change",
        text: `Status changed from "${task.status}" to "${newStatus}" by ${authorName}`,
        oldValue: task.status,
        newValue: newStatus,
      });
      await updateTask(task.id, {
        status: newStatus,
        updatedByUid: user?.uid || "system"
      });
      if (onFieldSaved) {
        onFieldSaved("status", newStatus);
      }
    } catch (err) {
      console.error("Error saving status:", err);
    }
  };

  const handlePriorityChange = async (newPriority) => {
    if (newPriority === task.priority) return;
    try {
      const authorName = user?.displayName || user?.email || "System";
      await addComment(task.id, {
        authorUid: user?.uid || "system",
        authorName: authorName,
        isSystem: true,
        type: "state_change",
        text: `Priority changed from "${task.priority}" to "${newPriority}" by ${authorName}`,
        oldValue: task.priority,
        newValue: newPriority,
      });
      await updateTask(task.id, {
        priority: newPriority,
        updatedByUid: user?.uid || "system"
      });
      if (onFieldSaved) {
        onFieldSaved("priority", newPriority);
      }
    } catch (err) {
      console.error("Error saving priority:", err);
    }
  };

  const handleRecurrenceChange = async (newRecurrence) => {
    try {
      await updateTask(task.id, {
        recurrence: newRecurrence,
        updatedByUid: user?.uid || "system"
      });
      if (onFieldSaved) {
        onFieldSaved("recurrence", newRecurrence);
      }
    } catch (err) {
      console.error("Error saving recurrence:", err);
    }
  };

  const handleLabelsChange = async (newLabels) => {
    try {
      await updateTask(task.id, {
        labels: newLabels,
        updatedByUid: user?.uid || "system"
      });
      if (onFieldSaved) {
        onFieldSaved("labels", newLabels);
      }
    } catch (err) {
      console.error("Error saving labels:", err);
    }
  };

  const handleRemoveLabel = async (labelName) => {
    const newLabels = (task.labels || []).filter((l) => l !== labelName);
    await handleLabelsChange(newLabels);
  };

  return (
    <div className="space-y-6">
      {/* Parameters List Card */}
      <div className="bg-gray-50 border border-gray-150 rounded-xl p-4 space-y-4">


        {/* Assignee Row */}
        <div 
          className="grid grid-cols-[100px_1fr] items-center text-xs relative"
          onMouseEnter={() => setHoverWarn("assignee")}
          onMouseLeave={() => setHoverWarn(null)}
        >
          <span className="text-gray-500 font-bold">ASSIGNEE</span>
          <div>
            <UserSelectDropdown
              users={users}
              value={task.assigneeUid || ""}
              onChange={handleAssigneeChange}
              buttonClassName="bg-transparent hover:bg-gray-100 px-2 py-1 rounded cursor-pointer font-bold text-gray-850 focus:outline-none transition-colors border border-transparent hover:border-gray-200 min-w-[120px] text-left flex justify-between items-center"
              dropdownAlign="right"
            />
          </div>
          {hoverWarn === "assignee" && <div className="absolute -top-4 left-0 text-[10px] text-orange-500 bg-orange-50 px-1 border border-orange-100 rounded z-10 whitespace-nowrap shadow-sm pointer-events-none">Changes will be logged</div>}
        </div>

        {/* Date Row */}
        <div 
          className="grid grid-cols-[100px_1fr] items-center text-xs relative"
          onMouseEnter={() => setHoverWarn("dueDate")}
          onMouseLeave={() => setHoverWarn(null)}
        >
          <span className="text-gray-500 font-bold">DUE</span>
          <div>
            <DatePicker value={task.dueDate} onChange={handleDateChange} dropdownAlign="right" />
          </div>
          {hoverWarn === "dueDate" && <div className="absolute -top-4 left-0 text-[10px] text-orange-500 bg-orange-50 px-1 border border-orange-100 rounded z-10 whitespace-nowrap shadow-sm pointer-events-none">Changes will be logged</div>}
        </div>

        {/* Recurrence Row */}
        <div className="grid grid-cols-[100px_1fr] items-center text-xs">
          <span className="text-gray-500 font-bold">REPEAT</span>
          <div>
            <RecurrencePicker value={task.recurrence || null} onChange={handleRecurrenceChange} dropdownAlign="right" />
          </div>
        </div>

        {/* Priority Row */}
        <div 
          className="grid grid-cols-[100px_1fr] items-center text-xs relative"
          onMouseEnter={() => setHoverWarn("priority")}
          onMouseLeave={() => setHoverWarn(null)}
        >
          <span className="text-gray-500 font-bold">PRIORITY</span>
          <div>
            <PriorityPicker value={task.priority} onChange={handlePriorityChange} dropdownAlign="right" />
          </div>
          {hoverWarn === "priority" && <div className="absolute -top-4 left-0 text-[10px] text-orange-500 bg-orange-50 px-1 border border-orange-100 rounded z-10 whitespace-nowrap shadow-sm pointer-events-none">Changes will be logged</div>}
        </div>

        {/* Status Row */}
        <div 
          className="grid grid-cols-[100px_1fr] items-center text-xs relative"
          onMouseEnter={() => setHoverWarn("status")}
          onMouseLeave={() => setHoverWarn(null)}
        >
          <span className="text-gray-500 font-bold">STATUS</span>
          <div>
            <select
              value={task.status || "Open"}
              onChange={(e) => handleStatusChange(e.target.value)}
              className="bg-transparent hover:bg-gray-100 px-2 py-1 rounded cursor-pointer font-bold text-gray-850 focus:outline-none transition-colors border border-transparent hover:border-gray-200 min-w-[120px] text-left flex justify-between items-center w-full"
            >
              <option value="Open">Open</option>
              <option value="In Progress">In Progress</option>
              <option value="On Hold">On Hold</option>
              <option value="Closed">Closed</option>
            </select>
          </div>
          {hoverWarn === "status" && <div className="absolute -top-4 left-0 text-[10px] text-orange-500 bg-orange-50 px-1 border border-orange-100 rounded z-10 whitespace-nowrap shadow-sm pointer-events-none">Changes will be logged</div>}
        </div>

        {/* Labels Row */}
        <div 
          className="grid grid-cols-[100px_1fr] items-start text-xs pt-1"
        >
          <span className="text-gray-500 font-bold mt-1">LABELS</span>
          <div className="flex flex-wrap gap-1.5 min-h-[28px] p-1">
            {(task.labels || []).length > 0 ? (
              (task.labels || []).map((name) => {
                const labelColor = labels.find(l => l.name.toLowerCase() === name.toLowerCase())?.color;
                return (
                  <LabelTag
                    key={name}
                    name={name}
                    color={labelColor}
                    onRemove={(n) => handleRemoveLabel(n)}
                  />
                );
              })
            ) : (
              <span className="text-gray-400 italic font-medium select-none">Type @ in title to add</span>
            )}
          </div>
        </div>

      </div>

      {/* Footer Metadata */}
      <div className="border-t border-gray-100 pt-4 px-1 space-y-2 text-[10px] text-gray-400 font-semibold select-none">
        <div>
          <span>Created by: </span>
          <span className="text-gray-600 font-bold">{getUserName(task.createdByUid)}</span>
        </div>
        <div>
          <span>Created at: </span>
          <span className="text-gray-550">{formatMetadataTime(task.createdAt)}</span>
        </div>
        <div>
          <span>Last updated: </span>
          <span className="text-gray-550">{formatMetadataTime(task.updatedAt)}</span>
        </div>
        <div>
          <span>Last updated by: </span>
          <span className="text-gray-600 font-bold">{getUserName(task.updatedByUid)}</span>
        </div>
      </div>
    </div>
  );
}
